import React, { useState } from 'react';
import { Chart, ArcElement, CategoryScale, LinearScale, Title, Tooltip, Legend, DoughnutController } from 'chart.js';
import { Box, Typography, Link } from '@mui/material';
import DoughnutChartComponent from './DoughnutChartComponent';
import PieChartModal from './PieChartModal';

// Register Chart.js components
Chart.register(ArcElement, CategoryScale, LinearScale, Title, Tooltip, Legend, DoughnutController);

/**
 * Displays a doughnut chart of portfolio sector allocation
 * Matched to the exact styling of the existing allocation chart
 * 
 * @param {Object} props
 * @param {Object} props.sectorData - Object with sector names as keys and percentage values
 * @param {String} props.title - Chart title
 */
const SectorDoughnutChart = ({ sectorData, title = "Sector Allocation" }) => {
    const [open, setOpen] = useState(false);
    const [dataArray, setDataArray] = useState([]);

    // Colors for sectors - using more professional financial sector colors
    const sectorColors = {
        'Technology': '#36A2EB',
        'Healthcare': '#FF5733',
        'Financial Services': '#9966FF',
        'Consumer Cyclical': '#FFCE56',
        'Industrials': '#566573',
        'Communication Services': '#8E44AD',
        'Consumer Defensive': '#E67E22',
        'Energy': '#2E86C1',
        'Basic Materials': '#CB4335',
        'Real Estate': '#16A085',
        'Utilities': '#D35400',
        'Other': '#4BC0C0'
    };

    // Default colors for sectors not in the map
    const defaultColors = [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
        '#FF5733', '#00BFFF', '#FFD700', '#32CD32', '#FF1493', '#1E90FF'
    ];

    const handleOpen = () => {
        // Format the data for the modal table
        const dataArray = Object.entries(sectorData)
            .map(([label, value]) => ({
                label,
                value: `${value.toFixed(1)}%`,
                percentage: value.toFixed(1)
            }))
            .sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage));

        setDataArray(dataArray);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    if (!sectorData || Object.keys(sectorData).length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '250px' }}>
                <Typography>No sector data available</Typography>
            </Box>
        );
    }

    // Format the data for the chart
    const processedData = {};
    Object.entries(sectorData).forEach(([sector, value]) => {
        // Round percentages to 1 decimal place
        processedData[sector] = parseFloat(value.toFixed(1));
    });

    // Prepare chart data
    const labels = Object.keys(processedData);
    const datasetData = Object.values(processedData);

    // Assign colors to sectors
    const backgroundColor = labels.map((sector, index) => {
        return sectorColors[sector] || defaultColors[index % defaultColors.length];
    });

    const chartData = {
        labels: labels,
        datasets: [
            {
                data: datasetData,
                backgroundColor: backgroundColor,
                hoverBackgroundColor: backgroundColor,
                datalabels: {
                    display: false,
                },
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                right: 40 // Add padding to prevent overflow
            }
        },
        plugins: {
            legend: {
                position: 'right',
                align: 'center',
                labels: {
                    boxWidth: 10,
                    padding: 10,
                    generateLabels: function (chart) {
                        const labels = Chart.overrides.doughnut.plugins.legend.labels.generateLabels(chart);
                        return labels.slice(0, 6); // Show only top 6 sectors in legend
                    }
                },
                onClick: () => false
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (tooltipItem) {
                        const label = tooltipItem.label || '';
                        const value = tooltipItem.raw || 0;
                        return `${label}: ${value.toFixed(1)}%`;
                    }
                }
            },
            datalabels: {
                display: (context) => {
                    return context.active ? 'auto' : false;
                },
                formatter: (value, context) => {
                    return `${value.toFixed(1)}%`;
                },
                color: '#fff',
                font: {
                    weight: 'bold',
                }
            }
        },
        animation: {
            animateScale: true,
            animateRotate: true
        }
    };

    return (
        <div>
            <Typography variant="subtitle1" >{title}</Typography>
            <div style={{ height: '200px', position: 'relative', paddingRight: '20px' }}>
                <DoughnutChartComponent chartData={chartData} options={options} />
            </div>
            <Box sx={{ textAlign: 'right' }}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={handleOpen}
                    underline="hover"
                >
                    View All
                </Link>
            </Box>
            <PieChartModal
                open={open}
                handleClose={handleClose}
                dataArray={dataArray}
                headline={title}
                columns={[
                    { id: 'label', label: 'Sector' },
                    { id: 'percentage', label: 'Percentage %' }
                ]}
            />
        </div>
    );
};

export default SectorDoughnutChart;